import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Shield, Save, Loader2, ToggleLeft, ToggleRight, Eye, Edit3, Award, Tags, UserCheck } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const AdminSecuritySettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Security settings state
  const [securitySettings, setSecuritySettings] = useState({
    company_admin_view_tier_info: true,
    company_admin_update_tier_info: true,
    company_admin_manual_tier_override: true,
    company_admin_manage_account_types: true,
    company_admin_login_as_user: false
  });

  // Only main admin can access this page
  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  useEffect(() => {
    if (isMainAdmin) {
      fetchSecuritySettings();
    }
  }, [isMainAdmin]);

  const fetchSecuritySettings = async () => {
    try {
      const response = await api.get('/admin/settings/security');
      if (response.data) {
        setSecuritySettings({
          company_admin_view_tier_info: response.data.company_admin_view_tier_info ?? true,
          company_admin_update_tier_info: response.data.company_admin_update_tier_info ?? true,
          company_admin_manual_tier_override: response.data.company_admin_manual_tier_override ?? true,
          company_admin_manage_account_types: response.data.company_admin_manage_account_types ?? true,
          company_admin_login_as_user: response.data.company_admin_login_as_user ?? false
        });
      }
    } catch (error) {
      console.error('Failed to fetch security settings:', error);
      // Use defaults if fetch fails
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/security', { security_settings: securitySettings });
      toast.success('Security settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save security settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleSetting = (key) => {
    setSecuritySettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isMainAdmin) {
    return (
      <Sidebar>
        <div className="p-8 bg-gray-50/50 min-h-screen">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800">Access Denied</h2>
            <p className="text-red-600 mt-2">Only Main Administrators can access security settings.</p>
          </div>
        </div>
      </Sidebar>
    );
  }

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 bg-gray-50/50 min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-red-100 rounded-xl">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Security Settings</h1>
            <p className="text-gray-500">Control platform-wide security permissions for company administrators</p>
          </div>
        </div>

        {/* Security Settings Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Award className="w-5 h-5 text-red-600" />
              Company Admin Tier Permissions
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Control what Company Administrators can do with user tier information
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Setting 1: View Tier Info */}
            <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">View Tier Information</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow Company Admins to view user tier information and verification status.
                    <br />
                    <span className="text-gray-500 italic">If disabled, tier info sections will be hidden from Company Admins.</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('company_admin_view_tier_info')}
                className={`flex-shrink-0 relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  securitySettings.company_admin_view_tier_info 
                    ? 'bg-blue-600' 
                    : 'bg-gray-300'
                }`}
                data-testid="toggle-view-tier"
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                    securitySettings.company_admin_view_tier_info 
                      ? 'translate-x-7' 
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Status indicator for View */}
            <div className="flex items-center gap-2 px-4">
              {securitySettings.company_admin_view_tier_info ? (
                <>
                  <ToggleRight className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">Enabled</span>
                  <span className="text-sm text-gray-500">— Company Admins can view tier information</span>
                </>
              ) : (
                <>
                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Disabled</span>
                  <span className="text-sm text-gray-500">— Tier information is hidden from Company Admins</span>
                </>
              )}
            </div>

            <hr className="border-gray-200" />

            {/* Setting 2: Update Tier Info */}
            <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Edit3 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Update Tier Information</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow Company Admins to edit user tier information and verification data.
                    <br />
                    <span className="text-gray-500 italic">If disabled, tier info will be visible but read-only for Company Admins.</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('company_admin_update_tier_info')}
                className={`flex-shrink-0 relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
                  securitySettings.company_admin_update_tier_info 
                    ? 'bg-amber-600' 
                    : 'bg-gray-300'
                }`}
                data-testid="toggle-update-tier"
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                    securitySettings.company_admin_update_tier_info 
                      ? 'translate-x-7' 
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Status indicator for Update */}
            <div className="flex items-center gap-2 px-4">
              {securitySettings.company_admin_update_tier_info ? (
                <>
                  <ToggleRight className="w-5 h-5 text-amber-600" />
                  <span className="text-sm font-medium text-amber-600">Enabled</span>
                  <span className="text-sm text-gray-500">— Company Admins can edit tier information</span>
                </>
              ) : (
                <>
                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Disabled</span>
                  <span className="text-sm text-gray-500">— Tier information is read-only for Company Admins</span>
                </>
              )}
            </div>

            <hr className="border-gray-200" />

            {/* Setting 3: Manual Tier Override */}
            <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Award className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Manual Tier Override</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow Company Admins to manually override user tier levels without verification.
                    <br />
                    <span className="text-gray-500 italic">If disabled, the manual tier override option will be hidden from Company Admins.</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('company_admin_manual_tier_override')}
                className={`flex-shrink-0 relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                  securitySettings.company_admin_manual_tier_override 
                    ? 'bg-red-600' 
                    : 'bg-gray-300'
                }`}
                data-testid="toggle-manual-override"
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                    securitySettings.company_admin_manual_tier_override 
                      ? 'translate-x-7' 
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Status indicator for Override */}
            <div className="flex items-center gap-2 px-4">
              {securitySettings.company_admin_manual_tier_override ? (
                <>
                  <ToggleRight className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-red-600">Enabled</span>
                  <span className="text-sm text-gray-500">— Company Admins can manually override tier levels</span>
                </>
              ) : (
                <>
                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Disabled</span>
                  <span className="text-sm text-gray-500">— Manual tier override is hidden from Company Admins</span>
                </>
              )}
            </div>

            <hr className="border-gray-200" />

            {/* Setting 4: Manage Account Types */}
            <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Tags className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Manage Account Types</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow Company Admins to view and edit Account Types.
                    <br />
                    <span className="text-gray-500 italic">If disabled, the Account Types link will be hidden from the sidebar and the page will be inaccessible.</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('company_admin_manage_account_types')}
                className={`flex-shrink-0 relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                  securitySettings.company_admin_manage_account_types 
                    ? 'bg-emerald-600' 
                    : 'bg-gray-300'
                }`}
                data-testid="toggle-account-types"
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                    securitySettings.company_admin_manage_account_types 
                      ? 'translate-x-7' 
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Status indicator for Account Types */}
            <div className="flex items-center gap-2 px-4">
              {securitySettings.company_admin_manage_account_types ? (
                <>
                  <ToggleRight className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-600">Enabled</span>
                  <span className="text-sm text-gray-500">— Company Admins can view and manage Account Types</span>
                </>
              ) : (
                <>
                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Disabled</span>
                  <span className="text-sm text-gray-500">— Account Types page is hidden from Company Admins</span>
                </>
              )}
            </div>

            <hr className="border-gray-200" />

            {/* Setting 5: Login as User */}
            <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <UserCheck className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Login as User</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow Company Admins to use the "Login as User" feature to impersonate users in their company.
                    <br />
                    <span className="text-gray-500 italic">If disabled, the Login as User button will not function for Company Admins. Super Admins always have this ability.</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('company_admin_login_as_user')}
                className={`flex-shrink-0 relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  securitySettings.company_admin_login_as_user
                    ? 'bg-purple-600'
                    : 'bg-gray-300'
                }`}
                data-testid="toggle-login-as-user"
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                    securitySettings.company_admin_login_as_user
                      ? 'translate-x-7'
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Status indicator for Login as User */}
            <div className="flex items-center gap-2 px-4">
              {securitySettings.company_admin_login_as_user ? (
                <>
                  <ToggleRight className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-600">Enabled</span>
                  <span className="text-sm text-gray-500">— Company Admins can login as users in their company</span>
                </>
              ) : (
                <>
                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Disabled</span>
                  <span className="text-sm text-gray-500">— Login as User is restricted to Super Admins only</span>
                </>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition disabled:opacity-50"
              data-testid="save-security-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Security Settings
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <h3 className="font-medium text-blue-800 mb-2">How these settings work:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>View Tier Info:</strong> Controls whether Company Admins can see the tier verification sections on user pages.</li>
            <li>• <strong>Update Tier Info:</strong> Controls whether Company Admins can edit tier data. If View is enabled but Update is disabled, they see read-only data.</li>
            <li>• <strong>Manual Override:</strong> Controls access to the manual tier level override feature, which bypasses normal verification.</li>
            <li>• <strong>Manage Account Types:</strong> Controls whether Company Admins can access the Account Types page to view/edit account types.</li>
            <li>• <strong>Login as User:</strong> Controls whether Company Admins can impersonate users in their company. Super Admins always have this ability.</li>
          </ul>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminSecuritySettings;
